import React, { useState } from 'react'
import { ViewersTableProps, viewersTableWidths } from './ViewersTable.types'
import styles from './ViewersTable.module.scss'
import cl from 'classnames'
import {
  TableBodyContainer,
  TableBodyLine,
  TableContainer,
  TableHeader,
  Text1
} from 'components/shared'
import { observer } from 'mobx-react-lite'
import { inject } from 'mobx-react'
import {
  VIEWERS_STORE,
  VIEWERS_TABLE_STORE,
  ViewersTableSortProps,
  ROUTING_STORE
} from 'store'
import { AvatarImage } from 'components'
import { Sex } from 'utilities'
import { capitalize } from 'validate.js'
import { SortTabEnums } from '../SortTabs'
import tableBodyContainerStyle from '../../../../components/shared/TableBodyContainer/TableBodyContainer.module.scss'
import { Select } from 'components/shared/Select'

const ViewersTableComponent = (props: ViewersTableProps) => {
  const {
    className,
    style,
    [VIEWERS_STORE]: {
      selectedSortTab,
      setCurrentViewer
    },
    [VIEWERS_TABLE_STORE]: {
      getSortDirection,
      handleSortDirectionChange,
      isColumnMarked,
      searchProps,
      onSearchPropsChange,
      searchResults
    },
    [ROUTING_STORE]: {
      push
    }
  } = props

  const [filter, setFilter] = useState<string>('all')

  const isWithinDateRange = (lastTimeStamp: string, range: string) => {
    const now = new Date()
    const timestamp = new Date(lastTimeStamp)
    switch (range) {
      case 'today':
        return now.toDateString() === timestamp.toDateString()
      case 'past-week':
        return now.getTime() - timestamp.getTime() <= 7 * 24 * 60 * 60 * 1000
      case 'past-month':
        return now.getTime() - timestamp.getTime() <= 30 * 24 * 60 * 60 * 1000
      default:
        return true
    }
  }

  const handleDropdownOnSelect = (value:string) => {
    setFilter(value)
  }

  const filteredViewers = searchResults.filter(viewer => {
    return isWithinDateRange(viewer.lastTimeStamp, filter)
  })

  const filterOptions: Array<{label : string, value : string}> = [{
    value: 'all',
    label: 'All'
  },
  {
    value: 'today',
    label: 'Today'
  },
  {
    value: 'past-week',
    label: 'Past Week'
  },
  {
    value: 'past-month',
    label: 'Past Month'
  }]

  const scrollbarSpacing = 4.8
  return (
    <TableContainer
      className={cl(styles.tableContainer, className)}
      style={style}
    >
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <label style={{ fontSize: '16px', color: '#333', margin: '0px 10px' }}>
          Last Updated
        </label>
        <Select
          className={styles.dropDown}
          options={filterOptions}
          value={filter}
          onChange={handleDropdownOnSelect}
          noNoneOption
        />
      </div>
      <TableHeader
        tableColumns={[
          {
            label: 'full name',
            // widthPercentage: viewersTableWidths.name,
            sortDirection: getSortDirection(ViewersTableSortProps.NAME),
            setSortDirection: handleSortDirectionChange(ViewersTableSortProps.NAME),
            isColumnMarked: isColumnMarked(ViewersTableSortProps.NAME),
            inputProps: {
              placeholder: 'Search...',
              value: searchProps.fullName,
              onChange: fullName => onSearchPropsChange({ fullName })
            },
            widthPx: viewersTableWidths.name
          },
          {
            label: selectedSortTab,
            // widthPercentage: viewersTableWidths.dynamicCol,
            sortDirection: getSortDirection(ViewersTableSortProps.DYNAMIC_COL),
            setSortDirection: handleSortDirectionChange(ViewersTableSortProps.DYNAMIC_COL),
            isColumnMarked: isColumnMarked(ViewersTableSortProps.DYNAMIC_COL),
            widthPx: viewersTableWidths.dynamicCol
          },
          {
            label: 'email',
            // widthPercentage: viewersTableWidths.email,
            inputProps: {
              placeholder: 'Search...',
              value: searchProps.email,
              onChange: email => onSearchPropsChange({ email })
            },
            widthPx: viewersTableWidths.email
          },
          {
            label: 'gender',
            // widthPercentage: viewersTableWidths.gender,
            selectProps: {
              options: Object.values(Sex).map(sex => ({
                label: capitalize(sex.toLowerCase()),
                value: sex
              })),
              value: searchProps.sex,
              onChange: sex => onSearchPropsChange({ sex }),
              noneLabel: 'All'
            },
            widthPx: viewersTableWidths.gender
          },
          {
            label: 'dob, age',
            // widthPercentage: viewersTableWidths.age
            widthPx: viewersTableWidths.age
          },
          {
            label: 'address',
            // widthPercentage: viewersTableWidths.address
            widthPx: viewersTableWidths.address + scrollbarSpacing
          }
        ]}
      />
      <TableBodyContainer className={tableBodyContainerStyle.tableBodyContainer}>
        {filteredViewers.map(viewer => {
          const dynamicValue = {
            [SortTabEnums.TOTAL_WATCH]: viewer.totalWatchTimeAsString,
            [SortTabEnums.INTERACTION_TIME]: viewer.totalInteractionTimeAsString,
            [SortTabEnums.ADD_REVENUE]: viewer.totalAddRevenueAsString,
            [SortTabEnums.TOTAL_SPENT]: viewer.totalSpentAsString
          }[selectedSortTab]

          const handleViewerDetailsClick = () => {
            setCurrentViewer(viewer)
            push(`/viewers/${viewer.id}`)
          }

          return (
            <TableBodyLine
              key={viewer.id}
              cells={[
                {
                  // widthPercentage: viewersTableWidths.name,
                  children: (
                    <>
                      <AvatarImage src={viewer.imageUrl} size={34} />
                      <Text1 bold style={{ marginLeft: 11 }}>{viewer.fullName}</Text1>
                    </>
                  ),
                  onClick: handleViewerDetailsClick,
                  isColumnMarked: isColumnMarked(ViewersTableSortProps.NAME),
                  widthPx: viewersTableWidths.name
                },
                {
                  // widthPercentage: viewersTableWidths.dynamicCol,
                  children: dynamicValue,
                  isColumnMarked: isColumnMarked(ViewersTableSortProps.DYNAMIC_COL),
                  widthPx: viewersTableWidths.dynamicCol
                },
                {
                  // widthPercentage: viewersTableWidths.email,
                  children: <Text1 overflowEllipsis>{viewer.emailAddress}</Text1>,
                  widthPx: viewersTableWidths.email
                },
                {
                  // widthPercentage: viewersTableWidths.gender,
                  children: viewer.gender,
                  widthPx: viewersTableWidths.gender
                },
                {
                  // widthPercentage: viewersTableWidths.age,
                  children: `${viewer.birthDate}, ${viewer.age}`,
                  widthPx: viewersTableWidths.age
                },
                {
                  // widthPercentage: viewersTableWidths.address,
                  children: <Text1 overflowEllipsis>{viewer.fullAddress}</Text1>,
                  widthPx: viewersTableWidths.address
                }
              ]}
            />
          )
        })}
      </TableBodyContainer>
    </TableContainer>
  )
}

const ViewersTable = inject(
  VIEWERS_STORE,
  VIEWERS_TABLE_STORE,
  ROUTING_STORE
)(observer(ViewersTableComponent))
export { ViewersTable }
